// Pure helper that determines whether a deliverable carries a slip cue
// in the name-cell sub-line, and how many days the slip is. Two cues:
//   `late`           — past due, no submission yet (supplier chase).
//   `submitted_late` — submission arrived after the due date (the
//                      delivery was delayed but it's now with the
//                      customer for review).
// The lifecycle word `overdue` belongs to the status pill vocabulary;
// slip-tag variants use `late` / `submitted_late` per brand-guide.

const MS_PER_DAY = 86_400_000;

export type SlipVariant = "none" | "late" | "submitted_late";
export interface SlipResult {
  variant: SlipVariant;
  days: number;
}

function isoDay(iso: string): number | null {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function todayDay(today: Date): number {
  return Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
}

export function computeSlipVariant(
  deliverable: { dueDate?: string; submittedAt?: string | null },
  today: Date,
): SlipResult {
  const dueDate = deliverable.dueDate;
  if (!dueDate) return { variant: "none", days: 0 };
  const dueMs = isoDay(dueDate);
  if (dueMs === null) return { variant: "none", days: 0 };
  const submittedAt = deliverable.submittedAt ?? null;

  if (submittedAt) {
    const submittedMs = isoDay(submittedAt);
    if (submittedMs === null) return { variant: "none", days: 0 };
    if (submittedMs <= dueMs) return { variant: "none", days: 0 };
    return {
      variant: "submitted_late",
      days: Math.round((submittedMs - dueMs) / MS_PER_DAY),
    };
  }

  const todayMs = todayDay(today);
  if (todayMs <= dueMs) return { variant: "none", days: 0 };
  return {
    variant: "late",
    days: Math.round((todayMs - dueMs) / MS_PER_DAY),
  };
}
